<template>
  <v-container fluid>
    <v-card>
      <v-toolbar :dark="$vuetify.theme.dark" color="primary">
        <v-col cols="6" class="text-left">
          <v-toolbar-title class="textwithbackground--text">
            {{ $t('Список активных сессий') }}
          </v-toolbar-title>
        </v-col>
      </v-toolbar>
      <general-error v-if="!!generalError" />
      <v-row class="px-2 pt-2">
        <v-col>
          <v-expansion-panels>
            <v-expansion-panel v-test="{ panel: '' }">
              <v-expansion-panel-header>
                <div id="search">
                  <v-icon>
                    search
                  </v-icon>
                  {{ $t('Фильтры') }}
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager @keydown.native.enter="focusOnBtn('searchBtn')">
                <v-row>
                  <v-col
                      class="pl-2"
                      cols="12"
                      md="5"
                      lg="4"
                      xl="3">
                    <validatable-enum-combobox
                        v-model="params.lotType"
                        v-test="{ combobox: 'lotType' }"
                        clearable
                        :readonly="isLoading"
                        :values-enum="LOT_TYPE_ENUM"
                        :label="$t('Тип торговой процедуры')"
                        @change="focusOnBtn('searchBtn')" />
                  </v-col>
                  <v-col
                      class="pl-2"
                      cols="12"
                      md="5"
                      lg="4"
                      xl="3">
                    <validatable-text-input
                        v-model="params.lotNumber"
                        v-test="{ input: 'lotNumber' }"
                        rules="numeric"
                        :label="$t('№ торговой процедуры')"
                        :readonly="isLoading"
                        hide-details
                        @keydown.native.enter="focusOnBtn('searchBtn')" />
                  </v-col>
                  <v-col class="text-right" align-self="center">
                    <btn-icon-tip
                        id="search"
                        ref="searchBtn"
                        icon="search"
                        :disabled="isLoading"
                        icon-color="primary"
                        :tip="$t('Поиск')"
                        @click="search" />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
      <v-row class="px-2">
        <v-col>
          <paginated-data-table
              v-if="getActiveSessions"
              ref="paginatedTable"
              :headers="headers1"
              :items="sessions"
              :loading="isLoading"
              item-key="sessionId"
              :total-count="totalCount"
              :items-per-page="itemsPerPage"
              single-expand
              show-expand
              :request-method="getActiveSessions">
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.lotNumber="{ item }">
              <route-link-with-tip
                  :text="item.lotNumber"
                  :tip="$t('Просмотр торговой процедуры')"
                  :to="{ path: `${urlRootPath}/lot/${item.lotType}/${item.lotId}` }" />
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.sessionNumber="{ item }">
              {{ item.sessionNumber }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.sessionType="{ item }">
              {{ item.sessionType
                ? $t(SESSION_TYPE_ENUM[item.sessionType])
                : null }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.dateStart="{ item }">
              {{ item.dateStart
                ? $moment(item.dateStart).format('DD.MM.YYYY HH:mm')
                : null }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.dateEndWithProlongation="{ item }">
              {{ item.dateEndWithProlongation
                ? $moment(item.dateEndWithProlongation).format('DD.MM.YYYY HH:mm')
                : null }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.sessionId="{ item }">
              <route-btn-tip
                  :tip="$t('Просмотр торговой сессии')"
                  :to="{ path: `${urlRootPath}/lot/${item.lotType}/${item.lotId}/bidding` }"
                  icon="visibility" />
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <v-row class="text-left py-4">
                  <text-view
                      md="6"
                      multiline
                      :display="item.lotType
                        ? $t(LOT_TYPE_ENUM[item.lotType])
                        : null"
                      :label="$t('Тип торговой процедуры')" />
                  <text-view
                      md="6"
                      multiline
                      :display="item.lotDescription"
                      :label="$t('Описание торговой процедуры')" />
                  <text-array-view
                      v-if="item.invitedOrganizations.length"
                      md="6"
                      :display="item.invitedOrganizations.map(io => io.organizationName)"
                      :label="$t('Приглашенные участники')" />
                </v-row>
              </td>
            </template>
          </paginated-data-table>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>

  import BtnIconTip from '../../../reused/ui/BtnIconTip.vue'
  import TextArrayView from '../../../reused/ui/TextArrayView.vue'
  import RouteLinkWithTip from '../../../reused/ui/RouteLinkWithTip.vue'
  import ValidatableEnumCombobox from '../../../reused/ui/ValidatableEnumCombobox.vue'
  import ValidatableTextInput from '../../../reused/ui/ValidatableTextInput.vue'
  import TextView from '../../../reused/ui/TextView.vue'
  import RouteBtnTip from '../../../reused/ui/RouteBtnTip.vue'
  import PaginatedDataTable from '../../../reused/ui/PaginatedDataTable.vue'
  import GeneralError from '../../../reused/common/GeneralError.vue'
  import { LOT_TYPE_ENUM, SESSION_TYPE_ENUM } from '../../../../constants/enums'
  import { mapActions, mapState } from 'vuex'
  import { SESSIONS_MANAGEMENT } from '@/constants/store.js'

  export default {
    components: {
      BtnIconTip,
      TextArrayView,
      RouteLinkWithTip,
      ValidatableEnumCombobox,
      RouteBtnTip,
      TextView,
      PaginatedDataTable,
      ValidatableTextInput,
      GeneralError
    },
    data () {
      return {
        LOT_TYPE_ENUM,
        SESSION_TYPE_ENUM,
        isLoading: false,
        itemsPerPage: 10,
        params: {
          lotNumber: '',
          lotType: ''
        }
      }
    },
    computed: {
      ...mapState(SESSIONS_MANAGEMENT.NAMESPACE, {
        sessionsInfo: state => (state[SESSIONS_MANAGEMENT.STATE.ACTIVE] || {})
      }),
      totalCount () {
        return this.sessionsInfo.totalCount || 0
      },
      sessions () {
        return this.sessionsInfo.items || []
      },
      headers1 () {
        return [
          { text: this.$t('№ торговой процедуры'), align: 'start', sortable: false, value: 'lotNumber' },
          { text: this.$t('Номер сессии'), align: 'start', sortable: false, value: 'sessionNumber' },
          { text: this.$t('Тип сессии'), align: 'start', sortable: false, value: 'sessionType' },
          { text: this.$t('Дата начала'), align: 'start', sortable: false, value: 'dateStart' },
          { text: this.$t('Дата завершения'), align: 'start', sortable: false, value: 'dateEndWithProlongation' },
          { text: this.$t('Действия'), align: 'start', sortable: false, value: 'sessionId' },
          { text: '', value: 'data-table-expand' }
        ]
      }
    },
    created () {
      this.clearStore()
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(SESSIONS_MANAGEMENT.NAMESPACE, {
        getActiveSessionsAction: SESSIONS_MANAGEMENT.ACTIONS.GET_ACTIVE_SESSIONS,
        clearActiveSessionsAction: SESSIONS_MANAGEMENT.ACTIONS.CLEAR_ACTIVE_SESSIONS
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearActiveSessionsAction()
          ])
      },
      search: async function () {
        await this.$refs.paginatedTable.resetPaginationAndGetDataFromServer()
      },
      getActiveSessions: async function (params) {
        try {
          this.isLoading = true
          await this.getActiveSessionsAction({
            ...params,
            ...this.params
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      }
    }
  }
</script>
